import React, { Fragment } from "react";
import PaperAirplane from "src/icons/PaperAirplane";
import NoData from "src/icons/NoData";
import Planet from "src/icons/Planet";
import SadCloud from "src/icons/SadCloud";
import Satellite from "src/icons/Satellite";
import { useTranslate, T } from "@tolgee/react";

/**
 * Loader used on components doing async requests. It can show a animated loading icon, dim the background
 * and switch over to an error state if the component fails to load its resource.
 */

interface LoadingI {
  active?: boolean;
  addContainer?: boolean;
  background?: boolean;
  dimmer?: boolean;
  error?: any;
  lightMode?: boolean;
  loadingText?: string | JSX.Element;
  paddingX?: number;
  paddingY?: number;
}

const Loading = ({
  active,
  addContainer,
  background,
  dimmer,
  error,
  lightMode,
  loadingText,
  paddingX,
  paddingY,
}: LoadingI) => {
  const { t } = useTranslate();
  // If the loader is not actively loading or in an error state, it should not render anything
  if (!active && !error) return null;
  const renderContent = () => {
    if (!error) {
      // Default loader
      return (
        <Fragment>
          <img
            src="/images/ctm-logo-landscape.png"
            alt="CTM Logo"
            aria-hidden
          />
          {loadingText && <p>{loadingText}</p>}
        </Fragment>
      );
    }
    // If an error has a code, let's use that code for our checking
    if (error.code) error = error.code;
    switch (error) {
      case 403:
        return (
          <span className="loading-error-text">
            <T
              keyName={"components.loading.errors.noPermission"}
              defaultValue="You do not have the permission required to use this tool. Please contact <link>{emailAddress}</link> for more information."
              params={{
                link: <a href="mailto:na.online@travelctm.com" />,
                emailAddress: "na.online@travelctm.com",
              }}
            />
          </span>
        );
      case 404:
      case 204:
      case "noContent":
        return (
          <Fragment>
            <NoData
              aria-hidden
              style={{ marginBottom: "20px", maxHeight: "200px" }}
            />
            <h4 className="loading-error-header">
              {t({
                key: "components.loading.errors.noDataToShow",
                defaultValue: "No data to show",
              })}
            </h4>
            <span className="loading-error-text">
              {t({
                key: "components.loading.errors.dataNotAvailable",
                defaultValue: "There is no data available right now.",
              })}
            </span>
          </Fragment>
        );
      case "pdfError":
        return (
          <span>
            <T
              keyName={"components.loading.errors.invoiceUnavailable"}
              defaultValue="This invoice is currently unavailable. Please reach out to <link>{emailAddress}</link>."
              params={{
                link: <a href="mailto:na.online@travelctm.com" />,
                emailAddress: "na.online@travelctm.com",
              }}
            />
          </span>
        );
      case "noCityPairs":
        return (
          <Fragment>
            <PaperAirplane
              aria-hidden
              style={{ marginBottom: "20px", maxHeight: "200px" }}
            />
            <span className="loading-error-text">
              {t({
                key: "components.loading.errors.noSearchResults",
                defaultValue:
                  "No search results were returned for these city pairs.",
              })}
            </span>
          </Fragment>
        );
      case "noFlights":
        return (
          <Fragment>
            <PaperAirplane
              aria-hidden
              style={{ marginBottom: "20px", maxHeight: "200px" }}
            />
            <T
              keyName={"components.loading.errors.noActiveTravelersNextWeek"}
              defaultValue="<textBlock>It looks like there are no active </textBlock><textBlock>travelers for the next week.</textBlock>"
              params={{
                textBlock: <span className="loading-error-text" />,
              }}
            />
          </Fragment>
        );
      case "noTravellers":
        return (
          <Fragment>
            <Planet aria-hidden style={{ maxHeight: "150px" }} />
            <span className="loading-error-text">
              {t({
                key: "components.loading.errors.noActiveTravelersSelectedDates",
                defaultValue: "No active travelers within the selected dates.",
              })}
            </span>
          </Fragment>
        );
      case 500:
      case 503:
      case 504:
      case "serverError":
        return (
          <Fragment>
            <SadCloud aria-hidden />
            <h4 className="loading-error-header">
              {t({
                key: "components.loading.errors.serverErrorHeader",
                defaultValue: "Oh snap, something went wrong",
              })}
            </h4>

            <T
              keyName={"components.loading.errors.serverErrorText"}
              defaultValue="<textBlock>Looks like we were not able to connect to the server.</textBlock><textBlock>Please try again in a few minutes.</textBlock>"
              params={{
                textBlock: <span className="loading-error-text" />,
              }}
            />
          </Fragment>
        );
      default:
        return (
          <Fragment>
            <Satellite aria-hidden />
            <h4 className="loading-error-header">
              {t({ key: "components.loading.errors.defaultHeader" })}
            </h4>
            <T
              keyName={"components.loading.errors.defaultText"}
              defaultValue="<textBlock>We can't load anything right now.</textBlock><textBlock>Please try again in a few minutes.</textBlock>"
              params={{
                textBlock: <span className="loading-error-text" />,
              }}
            />
          </Fragment>
        );
    }
  };

  // Set dynamic/prop based styles if we are using the dimmer
  const style: any = {};
  if (dimmer) {
    style.backgroundColor = lightMode
      ? "rgba(255,255,255, 0.6)"
      : "rgba(0,0,0,0.6)";
    if (paddingX) {
      style.left = `${paddingX}px`;
      style.right = `${paddingX}px`;
    }
    if (paddingY) {
      style.top = `${paddingY}px`;
      style.bottom = `${paddingY}px`;
    }
    if (!error && active) style.zIndex = "999";
  }
  if (background) style.zIndex = "0";

  return (
    <div
      style={
        addContainer
          ? {
              width: "100%",
              height: "100%",
              minHeight: "500px",
              position: "relative",
            }
          : {}
      }
    >
      <div className="loading-container" style={style}>
        <div className="loading-centered-div">{renderContent()}</div>
      </div>
    </div>
  );
};

export default Loading;
